import { cacheSet } from "./cache";

export type StayGuestCount = 1 | 2 | 3 | 4;

export type LinkRecord = {
  id: string;
  guestName: string;
  validFrom: number;
  validUntil: number;
  createdAt: number;
  revoked: boolean;
  stayGuestCount: StayGuestCount;
  bookingReference?: string;
  bookingDate?: string;
  // Extra time after checkout the doors stay openable. Defaults are applied
  // server-side, so they're always present on the wire even for older links.
  gracePeriodEnabled: boolean;
  gracePeriodMinutes: number;
  // When false, the guest can open the doors without filling out the
  // registration form and the police-portal auto-submission is skipped.
  documentsRequired: boolean;
  // Registration state -- only present on the dashboard list response
  // (`/api/admin/links`); per-link mutations don't recompute and return these.
  registrationFilledCount?: number;
  registrationTotal?: number;
  registrationComplete?: boolean;
  lockedForEdit?: boolean;
  // Most recent failed alta that hasn't been superseded by a later success.
  // Drives the "Submission errors" widget on the admin dashboard. Absent
  // when there is no outstanding failure (the common case).
  lastFailure?: {
    at: string;
    message: string;
    httpStatus: number | null;
    mode: "mock" | "prod";
  };
};

export type Sexo = "H" | "M" | "O";
export type DocumentoTipo = "NIF" | "NIE" | "PASAPORTE" | "OTRO";

export type RegistrationPerson = {
  nombre: string;
  primerApellido: string;
  segundoApellido?: string;
  fechaNacimiento: string;
  nacionalidad: string;
  sexo: Sexo;
  documentoTipo: DocumentoTipo;
  documentoNumero: string;
  telefono?: string;
  correo?: string;
  updatedAt: string;
};

export type SlotMode = "self" | "same-as-booker";

export type RegistrationSlot = {
  mode: SlotMode;
  person?: RegistrationPerson;
  updatedAt: string;
};

export type RegistrationSnapshot = {
  bookingContact: RegistrationPerson | null;
  guests: Array<RegistrationSlot | null>;
};

export type RegistrationResponse = {
  stayGuestCount: StayGuestCount;
  registration: RegistrationSnapshot;
  registrationComplete: boolean;
  lockedForEdit?: boolean;
};

export type HospedajesSubmissionError = {
  codigo?: string;
  mensaje: string;
  campo?: string;
};

export type HospedajesOperation = "alta" | "baja";

export type SubmittedLinkSnapshot = {
  guestName: string;
  validFrom: number;
  validUntil: number;
  stayGuestCount: number;
  bookingReference: string | null;
  bookingDate: string | null;
};

export type HospedajesSubmission = {
  operation: HospedajesOperation;
  ok: boolean;
  mode: "mock" | "prod";
  loteId: string | null;
  // Set for baja entries: the alta lote being cancelled.
  cancelledLoteId?: string;
  httpStatus: number | null;
  endpoint: string;
  durationMs: number;
  requestSha256: string;
  errors: HospedajesSubmissionError[];
  rawResponseTruncated?: string;
  submittedAt: string;
  dismissedAt?: string;
  // Frozen copy of the Details fields at submission time. Absent on alta
  // rows written before the field existed.
  submittedSnapshot?: SubmittedLinkSnapshot;
};

// Mirrors api/src/lib/hospedajesScheduler.ts ScheduleState. The Clock badge
// in the admin UI renders a different label per kind; nextRunAt is the
// epoch-ms timestamp the badge tooltip / sub-line displays.
export type ScheduleState =
  | { kind: "already_sent"; loteId: string }
  | {
      kind: "not_applicable";
      reason: "revoked" | "expired" | "documents_not_required";
    }
  | { kind: "awaiting_data"; nextRunAt: number }
  | { kind: "scheduled"; nextRunAt: number }
  | { kind: "imminent"; nextRunAt: number }
  | { kind: "overdue"; nextRunAt: number }
  | { kind: "last_attempt_failed"; nextRunAt: number; message: string };

export type AdminRegistrationResponse = RegistrationResponse & {
  bookingReference?: string | null;
  bookingDate?: string | null;
  validFrom?: number;
  validUntil?: number;
  guestName?: string;
  documentsRequired?: boolean;
  submissions?: HospedajesSubmission[];
  // True while an outstanding Alta exists on the police side. Edits to the
  // booking metadata and registration are server-rejected with HTTP 409 and
  // error "locked_by_hospedajes" while this is true.
  lockedForEdit?: boolean;
  activeLoteId?: string | null;
  // When and in what state the next automatic submission will happen. Drives
  // the clock badge + scheduled-time sub-line in AdminLinkDetail.
  schedule?: ScheduleState;
};

export type PersonValidationError = { field: string; reason: string };

export type PersonInput = Omit<RegistrationPerson, "updatedAt">;

export type DocScanResult = {
  detected: boolean;
  documentoTipo: "NIF" | "NIE" | "PASAPORTE" | "OTRO" | null;
  documentoNumero: string | null;
  nombre: string | null;
  primerApellido: string | null;
  segundoApellido: string | null;
  fechaNacimiento: string | null;
  nacionalidad: string | null;
  sexo: "H" | "M" | "O" | null;
};

export type FeedbackRating = "sad" | "neutral" | "happy";

export type Feedback = {
  rating: FeedbackRating;
  comment?: string;
  submittedAt: string;
  updatedAt: string;
  bookingReviewClickedAt?: string;
};

export type FeedbackEntry = Feedback & {
  linkId: string;
  guestName: string | null;
};

export type GuestView = {
  guestName: string;
  validFrom: number;
  validUntil: number;
  revoked: boolean;
  active: boolean;
  feedback: Feedback | null;
  bookingReviewUrl: string;
  stayGuestCount: StayGuestCount;
  registration: RegistrationSnapshot;
  registrationComplete: boolean;
  lockedForEdit?: boolean;
  gracePeriodEnabled: boolean;
  gracePeriodMinutes: number;
  documentsRequired: boolean;
};

export type Door = "apartment" | "portal";

export type DoorAttemptReason =
  | "ok"
  | "expired"
  | "revoked"
  | "not_yet_active"
  | "not_registered"
  | "door_failed"
  | "auth_failed"
  | "lock_unreachable"
  | "operation_failed"
  | "operation_timeout";

export type EventRecord = {
  type:
    | "created"
    | "edited"
    | "revoked"
    | "unrevoked"
    | "viewed"
    | "door_attempt"
    | "feedback_submitted"
    | "feedback_deleted"
    | "booking_review_clicked"
    | "registration_booker_updated"
    | "registration_guest_updated"
    | "registration_completed"
    | "registration_admin_edited"
    | "hospedajes_submitted"
    | "hospedajes_cancelled";
  at: string;
  ip?: string;
  ipLocation?: string;
  userAgent?: string;
  door?: Door;
  success?: boolean;
  reason?: DoorAttemptReason;
  detail?: string;
  changes?: Record<string, { from: unknown; to: unknown }>;
  guestName?: string;
  validFrom?: number;
  validUntil?: number;
  rating?: FeedbackRating;
  comment?: string;
  slot?: number;
  source?: "guest" | "admin" | "cron";
  hospedajesMode?: "mock" | "prod";
  hospedajesLoteId?: string | null;
  hospedajesHttpStatus?: number | null;
  hospedajesEndpoint?: string;
  hospedajesDurationMs?: number;
  hospedajesRequestSha256?: string;
  hospedajesErrors?: HospedajesSubmissionError[];
};

export type DoorActionType = "open" | "lock" | "unlock";

export type DoorAction = {
  door: Door;
  action: DoorActionType;
  at: string;
  source: "admin" | "guest";
  success: boolean;
  reason: DoorAttemptReason;
  detail?: string;
  linkId?: string;
  guestName: string | null;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  const body = res.headers.get("content-type")?.includes("application/json")
    ? await res.json()
    : await res.text();
  if (!res.ok) {
    const message =
      typeof body === "object" && body && "error" in body
        ? String((body as { error: unknown }).error)
        : `http_${res.status}`;
    const detail =
      typeof body === "object" && body && "detail" in body
        ? String((body as { detail: unknown }).detail)
        : undefined;
    throw new ApiError(message, res.status, detail);
  }
  return body as T;
}

export class ApiError extends Error {
  status: number;
  detail?: string;
  constructor(message: string, status: number, detail?: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

export type LockStatus = {
  state: number;
  stateLabel: string;
  isConnected: boolean;
  batteryLevel?: number;
  isCharging?: boolean;
  lastStateChangedDate?: string;
};

export const api = {
  admin: {
    session: () =>
      request<{ authenticated: boolean }>("/api/admin/session"),
    login: (password: string) =>
      request<{ ok: true }>("/api/admin/login", {
        method: "POST",
        body: JSON.stringify({ password }),
      }),
    logout: () =>
      request<{ ok: true }>("/api/admin/logout", { method: "POST" }),
    listLinks: async () => {
      const res = await request<{ links: LinkRecord[] }>("/api/admin/links");
      cacheSet.links(res.links);
      return res;
    },
    createLink: (input: {
      id?: string;
      guestName: string;
      validFrom: number;
      validUntil: number;
      stayGuestCount: StayGuestCount;
      bookingReference?: string;
      bookingDate?: string;
      gracePeriodEnabled?: boolean;
      gracePeriodMinutes?: number;
      documentsRequired?: boolean;
    }) =>
      request<{ link: LinkRecord }>("/api/admin/links", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    revokeLink: (id: string) =>
      request<{ ok: true }>(`/api/admin/links/${id}`, {
        method: "DELETE",
      }),
    unrevokeLink: (id: string) =>
      request<{ ok: true }>(`/api/admin/links/${id}/unrevoke`, {
        method: "POST",
      }),
    deleteLink: (id: string) =>
      request<{ ok: true }>(`/api/admin/links/${id}/permanent`, {
        method: "DELETE",
      }),
    updateLink: (
      id: string,
      input: {
        guestName: string;
        validFrom: number;
        validUntil: number;
        stayGuestCount: StayGuestCount;
        bookingReference: string | null;
        bookingDate: string | null;
        gracePeriodEnabled: boolean;
        gracePeriodMinutes: number;
        documentsRequired: boolean;
      }
    ) =>
      request<{ link: LinkRecord }>(`/api/admin/links/${id}`, {
        method: "PUT",
        body: JSON.stringify(input),
      }),
    getRegistration: async (id: string) => {
      const res = await request<AdminRegistrationResponse>(
        `/api/admin/links/${id}/registration`
      );
      cacheSet.registration(id, res);
      return res;
    },
    putBookerRegistration: (id: string, input: PersonInput) =>
      request<RegistrationResponse>(
        `/api/admin/links/${id}/registration/bookingContact`,
        { method: "PUT", body: JSON.stringify(input) }
      ),
    clearBookerRegistration: (id: string) =>
      request<RegistrationResponse>(
        `/api/admin/links/${id}/registration/bookingContact`,
        { method: "DELETE" }
      ),
    putGuestSlotRegistration: (
      id: string,
      slot: number,
      input: { mode: "self"; person: PersonInput } | { mode: "same-as-booker" }
    ) =>
      request<RegistrationResponse>(
        `/api/admin/links/${id}/registration/guest/${slot}`,
        { method: "PUT", body: JSON.stringify(input) }
      ),
    clearGuestSlotRegistration: (id: string, slot: number) =>
      request<RegistrationResponse>(
        `/api/admin/links/${id}/registration/guest/${slot}`,
        { method: "DELETE" }
      ),
    submitHospedajes: (id: string) =>
      request<{ submission: HospedajesSubmission }>(
        `/api/admin/links/${id}/registration/submit-hospedajes`,
        { method: "POST" }
      ),
    cancelHospedajes: (id: string) =>
      request<{ submission: HospedajesSubmission }>(
        `/api/admin/links/${id}/registration/cancel-hospedajes`,
        { method: "POST" }
      ),
    // Mark the most recent failed alta as dismissed so it drops off the
    // dashboard errors widget without needing a successful resubmission.
    // The audit history is preserved (original row + activity log stay).
    dismissHospedajesFailure: (id: string) =>
      request<{ ok: true; dismissedAt: string }>(
        `/api/admin/links/${id}/registration/dismiss-failure`,
        { method: "POST" }
      ),
    hospedajesXmlUrl: (id: string) =>
      `/api/admin/links/${id}/registration/hospedajes.xml`,
    getEvents: async (id: string) => {
      const res = await request<{ events: EventRecord[] }>(
        `/api/admin/links/${id}/events`
      );
      cacheSet.events(id, res.events);
      return res;
    },
    lockStatus: (opts: { force?: boolean } = {}) =>
      request<{ status: LockStatus }>(
        `/api/admin/lock/apartment/status${opts.force ? "?force=1" : ""}`
      ),
    lockDoor: () =>
      request<{ ok: true }>("/api/admin/lock/apartment/lock", {
        method: "POST",
      }),
    unlockDoor: () =>
      request<{ ok: true }>("/api/admin/lock/apartment/unlock", {
        method: "POST",
      }),
    pullSpring: () =>
      request<{ ok: true }>("/api/admin/lock/apartment/pull-spring", {
        method: "POST",
      }),
    openPortal: () =>
      request<{ ok: true }>("/api/admin/portal/open", { method: "POST" }),
    portalHistory: (opts: { cursor?: string } = {}) =>
      request<{ actions: DoorAction[]; nextCursor?: string }>(
        `/api/admin/portal/history${opts.cursor ? `?cursor=${encodeURIComponent(opts.cursor)}` : ""}`
      ),
    apartmentHistory: (opts: { cursor?: string } = {}) =>
      request<{ actions: DoorAction[]; nextCursor?: string }>(
        `/api/admin/apartment/history${opts.cursor ? `?cursor=${encodeURIComponent(opts.cursor)}` : ""}`
      ),
    scanDocument: (input: {
      image: string;
      mediaType: "image/jpeg" | "image/png" | "image/webp";
    }) =>
      request<{ result: DocScanResult }>("/api/admin/scan-document", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    listFeedback: () =>
      request<{ feedback: FeedbackEntry[] }>("/api/admin/feedback"),
    linkFeedback: async (id: string) => {
      const res = await request<{ feedback: Feedback | null }>(
        `/api/admin/links/${id}/feedback`
      );
      cacheSet.feedback(id, res.feedback);
      return res;
    },
    deleteLinkFeedback: (id: string) =>
      request<{ ok: true }>(`/api/admin/links/${id}/feedback`, {
        method: "DELETE",
      }),
  },
  guest: {
    get: (token: string) =>
      request<GuestView>(`/api/guest/${token}`),
    getRegistration: (token: string) =>
      request<RegistrationResponse>(`/api/guest/${token}/registration`),
    putBookerRegistration: (token: string, input: PersonInput) =>
      request<RegistrationResponse>(
        `/api/guest/${token}/registration/bookingContact`,
        { method: "PUT", body: JSON.stringify(input) }
      ),
    putGuestSlotRegistration: (
      token: string,
      slot: number,
      input: { mode: "self"; person: PersonInput } | { mode: "same-as-booker" }
    ) =>
      request<RegistrationResponse>(
        `/api/guest/${token}/registration/guest/${slot}`,
        { method: "PUT", body: JSON.stringify(input) }
      ),
    clearGuestSlotRegistration: (token: string, slot: number) =>
      request<RegistrationResponse>(
        `/api/guest/${token}/registration/guest/${slot}`,
        { method: "DELETE" }
      ),
    scanDocument: (
      token: string,
      input: { image: string; mediaType: "image/jpeg" | "image/png" | "image/webp" }
    ) =>
      request<{ result: DocScanResult }>(
        `/api/guest/${token}/scan-document`,
        { method: "POST", body: JSON.stringify(input) }
      ),
    open: (token: string, door: Door) =>
      request<{ ok: true }>(`/api/guest/${token}/open`, {
        method: "POST",
        body: JSON.stringify({ door }),
      }),
    submitFeedback: (
      token: string,
      input: { rating: FeedbackRating; comment?: string }
    ) =>
      request<{ feedback: Feedback }>(`/api/guest/${token}/feedback`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    recordBookingReviewClick: (token: string) =>
      request<{ ok: true }>(`/api/guest/${token}/booking-review-clicked`, {
        method: "POST",
      }),
  },
};

// Fire-and-forget prefetch helpers. Safe to call on hover / touchstart: in
// flight requests are deduplicated per key, so a rapid burst of pointer
// events still results in one network request per endpoint.
const inFlight = new Map<string, Promise<unknown>>();
function dedupe<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const existing = inFlight.get(key) as Promise<T> | undefined;
  if (existing) return existing;
  const p = fn().finally(() => inFlight.delete(key));
  inFlight.set(key, p);
  return p;
}

export const prefetch = {
  // Warm everything the link-detail page needs (data + JS chunk) before
  // the user actually navigates. Errors are swallowed -- the live fetch
  // on mount will surface them if they persist.
  linkDetail: (id: string) => {
    void dedupe(`reg:${id}`, () => api.admin.getRegistration(id)).catch(
      () => {}
    );
    void dedupe(`events:${id}`, () => api.admin.getEvents(id)).catch(() => {});
    void dedupe(`feedback:${id}`, () => api.admin.linkFeedback(id)).catch(
      () => {}
    );
    void import("@/pages/AdminLinkDetail");
  },
};
